/**
 * Small localStorage-backed maps for image metadata that has to be readable
 * before the first paint. Cache Storage holds the bytes but is async, so a
 * reload could not know what to render without a synchronous layer.
 */

import { whenIdle } from "src/utils/schedulePrefetch.js";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const SEVEN_DAYS_MS = 7 * ONE_DAY_MS;
const MAX_ENTRIES = 400;

/**
 * @param {string} severity
 * @param {string} event
 * @param {object} [details]
 */
const logDecisionStoreEvent = (severity, event, details = {}) => {
  console.log(
    JSON.stringify({
      severity,
      source: "imageDecisionStore",
      event,
      ...details,
    })
  );
};

/**
 * A Map hydrated synchronously from localStorage and written back when idle.
 *
 * @param {{ storageKey: string, isValid: (value: unknown) => boolean,
 *   ttlMs?: number, maxEntries?: number, refreshOnSet?: boolean,
 *   supersedes?: string[] }} options - `isValid` rejects values that cannot
 *   survive a reload, keeping the store from being poisoned. `refreshOnSet:
 *   false` keeps an unchanged value's age. `supersedes` lists storage keys this
 *   map replaces, which nothing else would ever reclaim.
 * @returns {{ get: Function, has: Function, set: Function }}
 */
const createPersistedMap = ({
  storageKey,
  isValid,
  ttlMs = SEVEN_DAYS_MS,
  maxEntries = MAX_ENTRIES,
  refreshOnSet = true,
  supersedes = [],
}) => {
  /** key → { value, t } */
  const entries = new Map();
  let flushScheduled = false;

  for (const stale of supersedes) {
    try {
      window.localStorage.removeItem(stale);
    } catch {
      // Storage is unavailable; the stale key is not worth failing hydration for.
    }
  }

  const hydrate = () => {
    let raw;
    try {
      raw = window.localStorage.getItem(storageKey);
    } catch {
      return;
    }
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      const now = Date.now();
      for (const [key, entry] of Object.entries(parsed ?? {})) {
        if (!entry || now - entry.t > ttlMs) continue;
        if (!isValid(entry.value)) continue;
        entries.set(key, entry);
      }
    } catch (error) {
      logDecisionStoreEvent("WARNING", "hydrate_failed", { storageKey });
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        // Nothing to recover; the map simply starts empty.
      }
    }
  };

  const persist = () => {
    const now = Date.now();
    for (const [key, entry] of entries) {
      if (now - entry.t > ttlMs) entries.delete(key);
    }
    while (entries.size > maxEntries) {
      entries.delete(entries.keys().next().value);
    }

    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify(Object.fromEntries(entries))
      );
    } catch (error) {
      logDecisionStoreEvent("WARNING", "persist_failed", { storageKey });
    }
  };

  const scheduleFlush = () => {
    if (flushScheduled) return;
    flushScheduled = true;
    whenIdle(
      () => {
        flushScheduled = false;
        persist();
      },
      { timeout: 5000 }
    );
  };

  hydrate();

  return {
    /**
     * @param {string} key
     * @returns {*} The stored value, or undefined on a miss.
     */
    get: (key) => entries.get(key)?.value,

    /**
     * @param {string} key
     * @returns {boolean}
     */
    has: (key) => entries.has(key),

    /**
     * Forgets a key so the next read re-derives it.
     * @param {string} key
     */
    delete: (key) => {
      if (entries.delete(key)) scheduleFlush();
    },

    /** Forgets everything. */
    clear: () => {
      if (!entries.size) return;
      entries.clear();
      scheduleFlush();
    },

    /**
     * Records a value, moving it to the most-recent end of the eviction order.
     * @param {string} key
     * @param {*} value
     */
    set: (key, value) => {
      if (!key) return;
      if (!isValid(value)) {
        logDecisionStoreEvent("WARNING", "rejected_value", { storageKey, key });
        return;
      }
      const existing = entries.get(key);
      const t =
        !refreshOnSet && existing?.value === value ? existing.t : Date.now();
      entries.delete(key);
      entries.set(key, { value, t });
      scheduleFlush();
    },
  };
};

/** Must match `PROCESSED_PATH_PREFIX` in src/utils/imageCacheStorage.js. */
const PROCESSED_PATH_PREFIX = "/__img-cache__/processed/";

/**
 * Only an original candidate URL survives a reload. A `blob:` or `data:` value
 * is a derived rendering and is dead on the next load, and a "no image"
 * verdict may be transient, so both would be worse than a miss. The worker path
 * is absolute too, but it names a rendering rather than the card's candidate.
 * @param {unknown} value
 * @returns {boolean}
 */
const isPersistableSource = (value) =>
  typeof value === "string" &&
  value.startsWith("http") &&
  !value.includes(PROCESSED_PATH_PREFIX);

/**
 * @param {unknown} value
 * @returns {boolean}
 */
const isPalette = (value) =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  // A null ink is a failed reading, not an answer worth keeping for the TTL.
  !!value.ink;

/**
 * Which candidate a card's fallback queue settled on. Ages from the first
 * decision, so the queue is re-walked daily however often the card is seen.
 */
export const imageDecisions = createPersistedMap({
  storageKey: "stanza:img-decisions:v3",
  supersedes: ["stanza:img-decisions:v2"],
  isValid: isPersistableSource,
  ttlMs: ONE_DAY_MS,
  refreshOnSet: false,
});

/**
 * Which images have a stored derivative. Cache Storage can only answer that
 * asynchronously, which is one paint too late — this is the synchronous index
 * that lets a tile commit to its final art on its very first render.
 * Values are a constant; only membership matters.
 */
export const processedKeys = createPersistedMap({
  storageKey: "stanza:img-processed:v2",
  isValid: (value) => value === 1,
  maxEntries: 600,
});

/**
 * When each image's stored copies were last verified against their source, so
 * the check is throttled across loads rather than repeated on every visit.
 */
export const imageChecks = createPersistedMap({
  storageKey: "stanza:img-checks:v1",
  isValid: (value) => typeof value === "number",
  maxEntries: 600,
});

/** Extracted colors per image, so gradients are not recomputed on every load. */
export const imagePalettes = createPersistedMap({
  storageKey: "stanza:img-palettes:v3",
  isValid: isPalette,
  supersedes: ["stanza:img-palettes:v2"],
});
